import { BigNumber } from 'bignumber.js'
import { Dictionary } from 'lodash';
import { Platform, Alert } from 'react-native';

export * from './useResizeObserver';

export { RandomId } from './random';


const confirmDialog = (title: string, message: string = '', isAlert = false) => {
  // Web Fallback
  if (Platform.OS === 'web') {
    return Promise.resolve(!!window[isAlert ? 'alert' : 'confirm'](`${title}\n\n${message}`.trim()));
  }

  // Native (iOS/Android)
  return new Promise<boolean>((resolve) => {
    Alert.alert(
      title,
      message || undefined,
      [
        isAlert ? undefined as any : { text: 'Cancel', onPress: () => resolve(false), style: 'cancel' },
        { text: 'OK', onPress: () => resolve(true) },
      ].filter(x => x),
      { cancelable: false }
    );
  });
};

const alertDialog = async (title: string, message: string = '') => await confirmDialog(title, message, false);

const shadowGenerator: any = (shadow = 0.3, percentage = 25) => ({
  '$platform-android': {
    elevation: shadow * 25,
  },
  shadowColor: `rgba(0, 0, 0, ${percentage})`,
  shadowOffset: { width: 0, height: 3 },
  shadowOpacity: shadow,
  shadowRadius: 5,
  boxShadow: `0px 0px 0px rgba(0, 0, 0, ${shadow})`, // Box shadow for container in web
});

export const MiscUtils = {
  dialog: {
    alert: alertDialog,
    confirm: confirmDialog,
  },
  ui: {
    shadowGenerator,
  },
};

const cashFormat = (val: string | number | BigNumber, options?: { forceNegative?: boolean, decimalPlaces?: number }) => {
  const bn = BigNumber(val)
  if (bn.isZero() || bn.isNaN()) {
    return '-'
  }
  const decimalPlaces = options?.decimalPlaces !== undefined ? options.decimalPlaces : bn.isInteger() ? 0 : 2;
  const isNegative = !!options?.forceNegative || bn.isNegative()
  const fmted = '$' + bn.absoluteValue().toFormat(decimalPlaces)
  return isNegative ? `(${fmted})` : fmted
}

/**
 * Formats any date input into a standard YYYY-MM-DD string.
 * Stably supported across Web, Expo iOS, and Expo Android (Hermes & JSC).
 */
export const formatDate = (dateInput: any): string => {
  if (!dateInput) return '';

  let date: Date;

  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'number') {
    date = new Date(dateInput);
  } else if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();

    // Identify if the string is a standard ISO 8601 variant
    const isISO = trimmed.includes('T') || trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed);

    if (!isISO && trimmed.includes('-')) {
      // Safely apply the slash workaround ONLY for plain date strings (e.g., "YYYY-MM-DD")
      date = new Date(trimmed.replace(/-/g, '/'));
    } else {
      // Leave standard ISO strings and other formats untouched for native parsing
      date = new Date(trimmed);
    }
  } else {
    date = new Date(dateInput);
  }

  // Final safety check: Guard against "Invalid Date" objects
  if (isNaN(date.getTime())) {
    console.warn(`formatDate received an invalid date input: ${dateInput}`);
    return '';
  }

  // Extract local time components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const formatDateTimezoneSafe = (dateInput: any): string => {
  if (!dateInput) return '';

  // If it's a string, look for YYYY-MM-DD or YYYY/MM/DD patterns first
  if (typeof dateInput === 'string') {
    const match = dateInput.trim().match(/^(\d{4})[-\/\.](\d{1,2})[-\/\.](\d{1,2})/);
    if (match) {
      const [_, year, month, day] = match;
      return `${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
    }
  }

  // Fallback to native Date handling for actual Date objects, numbers, or odd string formats
  return formatDate(dateInput);
};

const MONTHS_FALLBACK = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Safely converts a value to a Date object.
 * new Date(string) is notoriously buggy across JS engines (Hermes/JSC).
 */
const toSafeDate = (dateInput: any): Date => {
  if (!dateInput) return new Date();
  if (dateInput instanceof Date) return dateInput;

  try {
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? new Date() : d;
  } catch (e) {
    // If it's a string, replace hyphens with slashes for better 
    // compatibility with older JSC engines on iOS.
    if (typeof dateInput === 'string') {
      const d = new Date(dateInput.replace(/-/g, '/'));
      return isNaN(d.getTime()) ? new Date() : d;
    }
    return new Date();
  }
};

export const monthDateFormate = (dateString: any, locale = 'en-US'): string => {
  const date = toSafeDate(dateString);
  const day = date.getDate();

  try {
    const options: Intl.DateTimeFormatOptions = { month: 'long' };
    const month = new Intl.DateTimeFormat(locale, options).format(date);
    return `${month} ${day}`;
  } catch (e) {
    // Fallback if Intl fails
    return `${MONTHS_FALLBACK[date.getMonth()]} ${day}`;
  }
};

export const monthYearFormat = (dateString: any, locale = 'en-US'): string => {
  const date = toSafeDate(dateString);

  try {
    const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (e) {
    // Fallback if Intl fails
    return `${MONTHS_FALLBACK[date.getMonth()]} ${date.getFullYear()}`;
  }
};

export const FormatUtils = {
  numeric: {
    percentage: (str: any) => `${str}%`
  },
  currency: {
    cashFormat
  },
  dateTime: {
    'yyyy-mm-dd': formatDate,
    'yyyy-mm-dd (timezone safe)': formatDateTimezoneSafe,
    'Month dd': monthDateFormate,
    'Month yyyy': monthYearFormat
  },
  regex: {
    financialAmount: /^\$?\d+(,?\d{3})*(\.\d{1,2})?$/,
  }
}

export const transformData = (data) => {
  return data.flatMap((item) =>
    Object.entries(item).map(([key, value], index) => ({
      text: key === 'amount' ? `$ ${value}` : `${value}`, // Add $ prefix for 'amount'
      bold: true,
      borderRightWidthFooter: index < Object.keys(item)?.length - 1 ? 1 : 0, // Add border except for the last item
    }))
  )
} 
// 18002600
export const transformFinancialPlanData = (inputData, actionFns: {
  markAsDone: (actionItem: any) => Promise<any>;
  edit: (actionItem: any) => Promise<any>;
  delete: (actionItem: any) => Promise<any>;
}) => {
  //console.log({inputData})
  return inputData?.map((item) => {
    // Prepare the new data structure
    return {
      category: item.task, // The task becomes the category
      description: item.goalName ?? '--', // The goal name becomes the description
      action: monthDateFormate(item?.dueDate), // Action is the same as the task
      status: {
        status: item.status.charAt(0).toUpperCase() + item.status.slice(1), // Capitalizing the status
      },
      'Mark as Done': {
        checkbox: {
          value: item.status === 'completed', // Assuming this field will be false initially
          onChange: async (checked: boolean) => actionFns.markAsDone({...item, status: checked ? 'completed' : 'incomplete'})
        }
      }, 
      actionButtons: {
        edit: async () => {
          console.log('Edit action for:', item.task)
        },
        delete: async () => {
          console.log('Delete action for:', item.task)
          await actionFns.delete(item);
        },
      },
    }
  })
}

export function convertToFinancialPlanSummary(data) {
  if(!data) return []
  // Extract values from the input object
  const { tasksDueThisMonth, tasksCompletedThisMonth, taskNextDue } = data;

  // Get the due year from taskNextDue
  const dueYear = new Date(taskNextDue?.dueDate)?.getFullYear();
  const currentYear = new Date().getFullYear(); // Get the current year

  // Determine the time based on the due year
  const time = (dueYear === currentYear) ? 'This Year' : 'Next Year';

  // Initialize an empty array for the result
  let financialPlanSummary = [
    { status: 'Completed', value: tasksCompletedThisMonth?.toString(), time: 'This month', priority: 'low' },
    { status: 'Task Due', value: tasksDueThisMonth?.toString(), time: 'This month', priority: 'low' },
  ];

  // Add the third object with due date info
  if(taskNextDue){
  financialPlanSummary.push({
    status: 'Next Due',
    value: monthDateFormate(taskNextDue?.dueDate) ,
    time: time,  // Adjusting the time based on whether it's this year or next year
    priority: 'low'
  });
}

  return financialPlanSummary;
}
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLookup = Object.fromEntries(months.map((k, v) => [k, v]));
export const convertAndFilterData = (
  data: Array<{ month: string; incomplete: number; completed: number }>,
  activePeriod: '1 month' | '3 months' | '6 months'
) => {
  // Convert the data to the required format
  const formattedData = data?.map((item) => ({
    month: item.month,
    completed: item.completed,
    total: item.completed + item.incomplete, // Calculate total as completed + incomplete
  }));
  const today = new Date();
  
  if (formattedData) {
    let splitIndex = formattedData.findIndex((card) => {
      const cardMonthNo = monthLookup[card.month];
      return (cardMonthNo >= today.getMonth());
    });
    splitIndex = Math.max(0, splitIndex);

    // Apply the filtering logic based on activePeriod
    let filteredData = splitIndex > 0 
      ? [...formattedData.slice(splitIndex), ...formattedData.slice(0, splitIndex)] 
      : formattedData
    if (activePeriod === '1 month') {
      filteredData = filteredData?.slice(0,1) // Take the last item for 1 month
    } else if (activePeriod === '3 months') {
      filteredData = filteredData?.slice(0,3) // Take the last 3 items for 3 months
    } else if (activePeriod === '6 months') {
      filteredData = filteredData?.slice(0,6) // Take the last 6 items for 6 months
    }
    return filteredData;
  } else {
    return formattedData;
  }
}

const isInvestingSavingDebtPayments = (str: string) => 
  ["Investing", "Saving", "Debt Payments"].some(prefix => str.startsWith(prefix));


export function transBudgetRecommendationformData(inputData) {
  return (inputData as any[] || []).map(item => {
    const changeRaw = safeBigNum(item.recommended).minus(safeBigNum(item.current));
    const isGain = !changeRaw.isNegative();
    const category = (item?.currentPropertyKey || '') as string;
    const categoryRegex = isInvestingSavingDebtPayments(category) ? /- Monthly.*/ : /.*Monthly -/;
    const output =  ({
      category: category.replace(categoryRegex, '').trim(),
      currentAmount: FormatUtils.currency.cashFormat(item.current),
      recommendedAmount: FormatUtils.currency.cashFormat(item.recommended),
      change: {
        value: FormatUtils.currency.cashFormat(changeRaw),
        raw: changeRaw.toFixed(2),//item.change.replace(/[^\d]/g, ''),
        isGain
      },
      currentPropertyKey: item.currentPropertyKey || ''
    })
    //return output
    return category.startsWith('Income') || category.includes('MonthlyNetCashFlow')? undefined as typeof output : output
  })
  .filter((x) => x);
}

type ParserConfig = {
  group: RegExp;
  decimal: RegExp;
  numeral: RegExp;
  index: Map<string, number>;
  minusSign: RegExp;
};

const localeNumberPartsMap: Record<string, ParserConfig> = {};

/**
 * Escapes regex special characters and handles whitespace flexibility.
 * If the string contains a space, it allows any whitespace character.
 */
const robustEscape = (str: string) => {
  const escaped = str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Replace any literal space in the format with a \s matcher to handle 
  // users typing normal spaces vs non-breaking spaces.
  return escaped.replace(/\s/g, '\\s');
};

export const getLocaleNumberParser = (locale: string): ParserConfig => {
  // 1. Return cached config if available
  if (localeNumberPartsMap[locale]) {
    return localeNumberPartsMap[locale];
  }

  try {
    const format = new Intl.NumberFormat(locale);
    // 2. Use a specific test number to extract all possible parts
    const parts = typeof format.formatToParts === 'function' 
      ? format.formatToParts(-12345.6) 
      : [];

    const numerals = Array.from({ length: 10 }).map((_, i) => format.format(i));
    const index = new Map(numerals.map((d, i) => [d, i]));

    // 3. Robust Part Extraction with Fallbacks
    const getPart = (type: string, fallback: string) => 
      parts.find((p) => p.type === type)?.value ?? fallback;

    const minusVal = getPart('minusSign', '-');
    const groupVal = getPart('group', ',');
    const decimalVal = getPart('decimal', '.');

    const config: ParserConfig = {
      // Use non-capturing groups (?:) to support multi-character symbols
      minusSign: new RegExp(`(?:${robustEscape(minusVal)})`),
      group: new RegExp(`(?:${robustEscape(groupVal)})`, 'g'),
      decimal: new RegExp(`(?:${robustEscape(decimalVal)})`),
      numeral: new RegExp(`[${numerals.map(robustEscape).join('')}]`, 'g'),
      index,
    };

    localeNumberPartsMap[locale] = config;
    return config;

  } catch (error) {
    // 4. Extreme Fallback: If Intl fails entirely, return US-English defaults
    console.warn(`Locale parser failed for ${locale}, falling back to defaults.`, error);
    return {
      minusSign: /-/,
      group: /,/g,
      decimal: /\./,
      numeral: /[0-9]/g,
      index: new Map(['0','1','2','3','4','5','6','7','8','9'].map((d, i) => [d, i])),
    };
  }
};
const isNumber = (n) => typeof n === 'number' && !isNaN(n) && isFinite(n);
export const IntlNumberParser = (numberStr: string, locale = 'en-us') => {
    if (isNumber(numberStr)) return numberStr;
    if (typeof numberStr !== 'string') return NaN;
    const localeParser = getLocaleNumberParser(locale);
    const DIRECTION_MARK = /\u061c|\u200e/g;
    return +numberStr
        .trim()
        .replace(DIRECTION_MARK, '')
        .replace(localeParser.group, '')
        .replace(localeParser.decimal, '.')
        .replace(localeParser.numeral, (d) => localeParser.index.get(d).toString())
        .replace(localeParser.minusSign, '-');
};
export const safeBigNum = (num: string | number | BigNumber) => {
    const bnInput = typeof num === 'string' ? IntlNumberParser(num) : num;
    const parsed = new BigNumber(bnInput);
    return parsed.isNaN() ? new BigNumber(0) : parsed;
};
export const isWeb = Platform.select({web: true, native: false});
export const isNative = !isWeb;